/**
 * ChatDrawer Component
 *
 * Right-side slide-over drawer for the chat interface.
 * Always mounted in the DOM to enable smooth CSS transitions.
 * Uses Portal to avoid AppShell overflow clipping.
 *
 * Owns its own conversation state via useConversations hook.
 * Delegates proposal approve/reject to AppShell via callback props.
 */

import type { Component } from "solid-js";
import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  Show,
} from "solid-js";
import { Portal } from "solid-js/web";
import type {
  ProjectId,
  ProposalId,
  ProposedActionId,
  BoardEvent,
  MessageId,
} from "../../api/types";
import type { ProposalOperationResult } from "../../hooks/useProposals";
import { useConversations, useToast } from "../../hooks";
import { useNavigate, useLocation } from "@solidjs/router";
import { useChatViewContext } from "../../contexts/ChatViewContext";
import { useChatCommand } from "../../contexts/ChatCommandContext";
import { modKey } from "../layout/shellShortcuts";
import {
  chatContextKey,
  chatSessions,
  rememberChatSession,
} from "../../stores/chatDrawerStore";
import { MessageThread } from "./MessageThread";
import { ChatInput } from "./ChatInput";
import { AiErrorBoundary } from "../ui";

export interface ChatDrawerProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Callback to close the drawer */
  onClose: () => void;
  /** Project ID for conversation context */
  projectId: ProjectId;
  /**
   * Display-ID prefix of the selected project, for chat entity references.
   * Passed in rather than read from ProjectContext: the drawer is mounted
   * outside ProjectProvider in AppShell.
   */
  projectPrefix?: string;
  /** Callback to approve a proposal inline (from useProposals) */
  onApproveProposal: (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => Promise<ProposalOperationResult>;
  /** Callback to reject a proposal inline (from useProposals) */
  onRejectProposal: (id: ProposalId) => Promise<ProposalOperationResult>;
  /** Callback after any proposal action (for refetching) */
  onProposalActioned?: () => void;
  /** Callback when AI creates a new proposal (so AppShell can refresh its count) */
  onProposalCreated?: () => void;
  /** Register a handler so the parent can forward WebSocket events */
  onRegisterWebSocketHandler?: (handler: (event: BoardEvent) => void) => void;
  /** Register a callback to create a new conversation from outside the drawer */
  onRegisterNewChat?: (handler: () => void) => void;
  /** Current user's display name (for avatar) */
  userName?: string;
  /** Current user's avatar image URL */
  userAvatarUrl?: string;
}

export const ChatDrawer: Component<ChatDrawerProps> = (props) => {
  const { showError } = useToast();
  const viewContext = useChatViewContext();
  const { pendingCommand, clearCommand } = useChatCommand();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    conversations,
    error: conversationsError,
    selectedConversation,
    selectedError,
    messages,
    isResponding,
    streamingContent,
    select,
    create,
    send,
    cancel,
    getProposal,
    applyProposalUpdate,
    refreshProposal,
    handleWebSocketEvent,
    deleteMessageAndResend,
    retryMessage,
  } = useConversations(() => props.projectId, {
    // The inline report for a dropped edit sits in the conversation the user
    // just left, so there is nothing on screen to say it twice (FLOW-818).
    onDroppedEdit: showError,
  });

  // Same rethrow hazard as `conversationsError` above, one resource over: with
  // the backend unreachable the resumed conversation's own fetch fails too, and
  // `title()` below reads this accessor on every render — the drawer panel is
  // always mounted (it slides on `translate-x`, it is not `<Show>`n), so that
  // read is live whether or not the drawer is open. Every read goes through
  // here, so a failed load degrades to "no conversation selected" instead of
  // blanking the app.
  const readySelected = () =>
    selectedError() ? undefined : selectedConversation();

  // Wrap approve/reject so the updated proposal flows into this drawer's
  // conversation store, re-rendering the inline card without a refetch.
  const handleInlineApprove = async (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => {
    const result = await props.onApproveProposal(id, actionIds);
    applyProposalUpdate(result.proposal);
    return result;
  };

  const handleInlineReject = async (id: ProposalId) => {
    const result = await props.onRejectProposal(id);
    applyProposalUpdate(result.proposal);
    return result;
  };

  // Forward WebSocket events to the conversation handler, and refresh only the
  // single proposal that changed (if it belongs to this conversation).
  const handleDrawerWsEvent = (event: BoardEvent) => {
    handleWebSocketEvent(event);
    if (
      event.type === "ProposalUpdated" ||
      event.type === "ProposalCompleted"
    ) {
      const { proposal_id } = event.payload;
      if (getProposal(proposal_id)) {
        void refreshProposal(proposal_id);
      }
    }
  };

  // Expose WebSocket handler so AppShell can forward events
  props.onRegisterWebSocketHandler?.(handleDrawerWsEvent);

  // Expose new-chat handler so AppShell can trigger new conversations
  props.onRegisterNewChat?.(() => void create());

  const contextLabel = createMemo(() => {
    const ctx = viewContext();
    switch (ctx.type) {
      case "work_item":
        return "Focusing on item";
      case "backlog": {
        const visible = ctx.visibleItemIds.length;
        const total = ctx.totalItemCount;
        if (visible !== total)
          return `Backlog \u00b7 ${visible} of ${total} items`;
        return `Backlog \u00b7 ${total} items`;
      }
      default:
        return null;
    }
  });

  const [showNewChatBanner, setShowNewChatBanner] = createSignal(false);
  const [bannerReason, setBannerReason] = createSignal<string | undefined>();

  let contentRef: HTMLDivElement | undefined;
  let previouslyFocused: HTMLElement | null = null;

  // On drawer open: start a new chat, unless this is the same context the
  // drawer was last opened in — then resume the chat already in progress there
  // (FLOW-696, superseding FLOW-526's "select the most recent conversation").
  //
  // A new chat is empty rather than eagerly created: `handleSend` creates the
  // conversation with the first message, so toggling the drawer open and shut
  // leaves no trail of empty conversations behind.
  //
  // `on` keeps this to the open transition — a context that shifts underneath
  // an open drawer (a backlog filter, say) must not discard what's being typed.
  const [sessionKey, setSessionKey] = createSignal<string | null>(null);

  createEffect(
    on(
      () => props.open,
      (open) => {
        if (!open) return;

        const key = chatContextKey(props.projectId, viewContext());
        setSessionKey(key);

        const sessions = chatSessions();
        if (key in sessions) {
          const resuming = sessions[key];
          // The remembered conversation can have been deleted from the chat
          // page in the meantime; fall back to a new chat rather than a 404.
          // FLOW-992: a Solid resource accessor rethrows its rejection when
          // read while errored, and this drawer is AppShell chrome with no
          // route boundary above it — the throw would reach the app-root
          // boundary and replace the whole viewport. Guarded here rather than
          // in `useConversations` because `ChatPage` reads `conversations()`
          // in render without consuming `error()` and relies on that rethrow
          // reaching its `RouteErrorBoundary`. A failed list is treated as
          // not-yet-loaded, which is the state the `!convs` branch below
          // already handles: the remembered chat is resumed rather than
          // discarded on the strength of a list that never arrived.
          const convs = conversationsError() ? undefined : conversations();
          if (
            resuming === null ||
            !convs ||
            convs.some((conv) => conv.id === resuming)
          ) {
            if (resuming !== (readySelected()?.id ?? null)) {
              select(resuming);
            }
            return;
          }
        }

        select(null);
        rememberChatSession(key, null);
      },
    ),
  );

  // Mirror the drawer's conversation into this context's session, so reopening
  // here resumes it — including the one `handleSend` just created. Only a real
  // conversation is recorded: `selectedConversation` is momentarily undefined
  // while its messages load, and writing that would forget the chat mid-load.
  // The empty chat is recorded by the effect above, where it is deliberate.
  createEffect(() => {
    const key = sessionKey();
    const conversationId = readySelected()?.id;
    if (key && conversationId) {
      rememberChatSession(key, conversationId);
    }
  });

  // Reset suggestion banner when conversation changes
  createEffect(() => {
    readySelected();
    setShowNewChatBanner(false);
  });

  // Focus management: capture previous focus on open, restore on close
  createEffect(() => {
    if (props.open) {
      previouslyFocused = document.activeElement as HTMLElement | null;
      // Delay to allow CSS transition to complete
      setTimeout(() => {
        const textarea = contentRef?.querySelector("textarea");
        textarea?.focus();
      }, 200);
    } else {
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
        previouslyFocused = null;
      }
    }
  });

  // Process pending chat commands from other components. The 300ms wait is
  // for the open transition, and a command must not outlive the project it
  // was typed in (FLOW-829): cleanup cancels a timer a project switch
  // invalidates, and the project stamp is re-checked across each await,
  // since both `create()` and `send()` read the live project.
  createEffect(() => {
    const command = pendingCommand();
    if (!command || !props.open) return;

    const stale = () =>
      pendingCommand() !== command ||
      (command.projectId !== undefined &&
        command.projectId !== props.projectId);

    const timer = setTimeout(async () => {
      if (stale()) return;

      try {
        if (!readySelected()) {
          await create();
        }
      } catch {
        // No conversation, so no message to carry an inline error — the toast
        // is the only report this failure has.
        showError("Failed to send suggestion to AI");
        clearCommand();
        return;
      }

      if (stale()) return;

      try {
        await send(command.message, command.context ?? viewContext());
      } catch {
        // Reported inline on the failed message (FLOW-692).
      }
      clearCommand();
    }, 300);

    onCleanup(() => clearTimeout(timer));
  });

  const handleSend = async (content: string) => {
    if (content === "/clear") {
      try {
        await create();
      } catch {
        showError("Failed to create new conversation.");
      }
      return;
    }

    if (!readySelected()) {
      await create();
    }

    try {
      const response = await send(content, viewContext());
      if (response.has_proposal) {
        props.onProposalCreated?.();
        if (response.proposal_id) {
          void refreshProposal(response.proposal_id);
        }
      }
      if (response.suggest_new_conversation) {
        setShowNewChatBanner(true);
        setBannerReason(response.suggestion_reason);
      }
    } catch {
      // Reported inline on the failed message, with a Retry button — a toast
      // on top of that says the same thing twice (FLOW-692).
    }
  };

  // Handle retrying a failed message (mirrors handleSend's error handling)
  const handleRetryMessage = async (messageId: MessageId) => {
    try {
      const response = await retryMessage(messageId, viewContext());
      if (response.has_proposal) {
        props.onProposalCreated?.();
        if (response.proposal_id) {
          void refreshProposal(response.proposal_id);
        }
      }
    } catch {
      // Same as handleSend: the message's inline error indicator is the only
      // report (FLOW-692).
    }
  };

  const title = () => readySelected()?.title || "Chat";

  return (
    <Portal>
      {/* Screen reader announcement */}
      <span class="sr-only" aria-live="polite">
        {props.open
          ? "AI Assistant drawer opened"
          : "AI Assistant drawer closed"}
      </span>

      {/* Backdrop - mobile only */}
      <div
        class={
          "fixed inset-0 z-[34] bg-scrim/40 md:hidden " +
          "transition-opacity duration-200 ease-in-out " +
          (props.open ? "opacity-100" : "opacity-0 pointer-events-none")
        }
        onClick={props.onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        data-chat-drawer
        role="complementary"
        aria-label="AI Assistant"
        class={
          "fixed inset-y-0 right-0 z-[35] " +
          "w-full md:w-96 " +
          "bg-surface border-l border-line " +
          "flex flex-col " +
          "transition-transform duration-200 ease-in-out " +
          (props.open ? "translate-x-0" : "translate-x-full")
        }
      >
        {/* Header */}
        <div class="h-14 flex items-center justify-between px-4 border-b border-line">
          <span class="text-sm font-medium text-ink-2 truncate">{title()}</span>
          <div class="flex items-center gap-1">
            <button
              onClick={() => void create()}
              class="p-2 text-ink-3 hover:text-ink hover:bg-sunken rounded transition-colors"
              aria-label="New conversation"
            >
              <NewConversationIcon />
            </button>
            <button
              onClick={() => {
                navigate("/chat", { state: { from: location.pathname } });
              }}
              class="p-2 text-ink-3 hover:text-ink hover:bg-sunken rounded transition-colors"
              aria-label="Expand to full page"
              title="Expand to full page"
            >
              <ExpandIcon />
            </button>
            <button
              onClick={props.onClose}
              class="p-2 text-ink-3 hover:text-ink hover:bg-sunken rounded transition-colors"
              aria-label="Close chat"
              aria-keyshortcuts="Meta+J Control+J"
              title={`Close chat (${modKey()}J)`}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Context indicator */}
        <Show when={contextLabel()}>
          {(label) => (
            <div class="px-4 py-1.5 bg-sunken border-b border-line flex items-center gap-1.5 text-xs text-ink-2">
              <ContextIcon />
              <span>{label()}</span>
            </div>
          )}
        </Show>

        {/* New conversation suggestion banner */}
        <Show when={showNewChatBanner()}>
          <div class="px-4 py-2 bg-signal-amber-tint border-b border-signal-amber/25 flex items-center justify-between gap-2 text-xs text-signal-amber">
            <span class="flex-1">
              {bannerReason() || "This conversation is getting long."} Starting
              fresh may improve responses.
            </span>
            <button
              onClick={() => {
                setShowNewChatBanner(false);
                void create();
              }}
              class="shrink-0 px-2 py-0.5 bg-surface hover:bg-sunken rounded text-ink font-medium transition-colors"
            >
              New Chat
            </button>
            <button
              onClick={() => setShowNewChatBanner(false)}
              class="shrink-0 p-0.5 text-signal-amber hover:text-ink rounded transition-colors"
              aria-label="Dismiss suggestion"
            >
              <DismissIcon />
            </button>
          </div>
        </Show>

        {/* Content */}
        <div ref={contentRef} class="flex-1 flex flex-col overflow-hidden">
          {/* The boundary sits inside the panel, below the header, so a thread
              render throw loses only the thread — "Close chat", "New
              conversation" and "Expand to full page" above stay usable. */}
          <AiErrorBoundary
            surface="Chat"
            manualPath="You can close this drawer and keep working on the board."
            onDismiss={props.onClose}
            resetKey={props.open}
          >
            <>
              <MessageThread
                messages={messages()}
                isResponding={isResponding()}
                streamingContent={streamingContent()}
                onApproveProposal={handleInlineApprove}
                onRejectProposal={handleInlineReject}
                onProposalActioned={props.onProposalActioned}
                getProposal={getProposal}
                userName={props.userName}
                userAvatarUrl={props.userAvatarUrl}
                viewContext={viewContext()}
                onDeleteMessageAndResend={deleteMessageAndResend}
                onRetryMessage={handleRetryMessage}
                onSendFollowUp={handleSend}
                projectId={props.projectId}
                projectPrefix={props.projectPrefix}
              />
              <ChatInput
                onSend={handleSend}
                disabled={isResponding()}
                onCancel={cancel}
                placeholder="Ask the AI assistant..."
              />
            </>
          </AiErrorBoundary>
        </div>
      </div>
    </Portal>
  );
};

const ContextIcon: Component = () => (
  <svg
    class="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const NewConversationIcon: Component = () => (
  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const DismissIcon: Component = () => (
  <svg
    class="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const CloseIcon: Component = () => (
  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const ExpandIcon: Component = () => (
  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4"
    />
  </svg>
);

export default ChatDrawer;
